import { View } from 'react-native';

import { formatCentsToDollars } from '@fhs/menu/src/utils';
import {
  ActionSheet,
  IconAdd,
  IconRemove,
  ListItemLinkPressable,
  ScrollView,
  Skeleton,
  Text,
  YStack,
  createMqStyles,
  tokens,
} from '@fhs/ui';

import { useGetGiftCardTransactionHistoryQuery } from '../../queries/use-get-gift-card-transaction-history';
import { useGiftCardContext } from '../../state/gift-card';
import { formatDate, groupTransactionsByMonth, maskCardNumber } from '../../utils/helpers';
import { GiftCardTransactionType } from '../../utils/types';
import { GiftCardInfo } from '../gift-card-info';
import { GiftCardsActionSheetHeader } from '../gift-cards-action-sheet-header';

type GiftCardsTransactionHistoryActionSheetProps = {
  isVisible: boolean;
  onClose?: () => void;
};

const getTransactionDescription = ({
  description,
  lastCardDigits,
}: {
  description: string;
  lastCardDigits: string;
}) => {
  return `${description} ${maskCardNumber({ lastDigits: lastCardDigits, totalDigits: 8 })}`;
};

export const GiftCardsTransactionHistoryActionSheet = ({
  onClose,
  isVisible,
}: GiftCardsTransactionHistoryActionSheetProps) => {
  const mqStyles = useMqStyles();
  const { formattedBalance, lastDigits } = useGiftCardContext();

  const {
    data: transactionHistoryData,
    isPending: isTransactionHistoryLoading,
    isError: isTransactionHistoryError,
  } = useGetGiftCardTransactionHistoryQuery({
    enabled: isVisible,
  });

  const transactionsGroupedByMonth = transactionHistoryData?.transactions
    ? groupTransactionsByMonth(transactionHistoryData.transactions)
    : {};

  const isLoading = isTransactionHistoryLoading;

  const renderTransactionContent = () => {
    if (isLoading) {
      return (
        <YStack style={{ padding: 16, gap: 12 }}>
          <Skeleton style={{ height: 16, width: '40%' }} />
          <Skeleton style={{ height: 16, width: '100%' }} />
          <Skeleton style={{ height: 16, width: '100%' }} />
          <Skeleton style={{ height: 16, width: '40%' }} />
          <Skeleton style={{ height: 16, width: '100%' }} />
        </YStack>
      );
    }

    if (isTransactionHistoryError) {
      return (
        <YStack style={{ padding: 16 }}>
          <Text style={{ color: tokens.colors.$error }}>
            Unable to load transaction history. Please try again later.
          </Text>
        </YStack>
      );
    }

    const monthEntries = Object.entries(transactionsGroupedByMonth);

    if (monthEntries.length === 0) {
      return (
        <YStack style={{ padding: 16, alignItems: 'center' }}>
          <Text style={{ color: tokens.colors.$houseLight }}>No transactions found.</Text>
        </YStack>
      );
    }

    return monthEntries.map(([month, transactions]) => (
      <YStack key={month} style={{ paddingVertical: 16 }}>
        <View style={{ paddingHorizontal: 16 }}>
          <Text style={{ fontSize: 16 }} weight="semibold">
            {month}
          </Text>
        </View>
        {transactions.map(transaction => (
          <ListItemLinkPressable
            key={transaction.id}
            titleFontWeight="semibold"
            minHeightStyle="content"
            isFirstItem={false}
            isLastItem={false}
            title={formatDate(new Date(transaction.date))}
            subtitle={getTransactionDescription({
              description: transaction.description,
              lastCardDigits: lastDigits,
            })}
            icon={
              transaction.type === GiftCardTransactionType.Reload ? (
                <IconAdd size={24} color={tokens.colors.$success} />
              ) : (
                <IconRemove size={24} color={tokens.colors.$error} />
              )
            }
            indicatorText={
              transaction.type === GiftCardTransactionType.Reload
                ? `+ ${formatCentsToDollars(transaction.amount)}`
                : (formatCentsToDollars(transaction.amount) ?? undefined)
            }
            indicatorTextWeight="semibold"
            indicatorTextStyle={{
              color:
                transaction.type === GiftCardTransactionType.Reload
                  ? tokens.colors.$success
                  : tokens.colors.$houseDark,
            }}
          />
        ))}
      </YStack>
    ));
  };

  const handleClose = onClose ?? (() => {});

  return (
    <ActionSheet
      onRequestClose={handleClose}
      onClose={handleClose}
      isVisible={isVisible}
      grabber={false}
      noPanGestureDetection
      containerStyle={{ height: '90%' }}
      drawerProps={{
        style: mqStyles.drawerPropsStyle,
      }}
    >
      <GiftCardsActionSheetHeader
        headerTitle="Transaction History"
        onClose={onClose}
        onPressPrev={onClose}
      />
      <ScrollView style={{ flex: 1 }}>
        <YStack style={mqStyles.contentWrapper}>
          <GiftCardInfo lastCardDigits={lastDigits} balance={formattedBalance} />
        </YStack>
        {renderTransactionContent()}
      </ScrollView>
    </ActionSheet>
  );
};

const useMqStyles = createMqStyles({
  drawerPropsStyle: {
    $base: {
      flex: 1,
      backgroundColor: tokens.colors.$white,
      borderRadius: 12,
    },
    $gteDesktop: {
      flex: 1,
      backgroundColor: tokens.colors.$white,
      borderRadius: 0,
    },
  },
  contentWrapper: {
    $base: {
      padding: 16,
    },
    $gteDesktopLg: {
      padding: 24,
    },
  },
});
